/**
 * PaymentStep — Compact payment form (mock)
 *
 * The Pay button is now rendered in the WidgetFooter (matching the Review screen design).
 * This component only renders the card form and amount due.
 */

import { useState } from 'preact/hooks'
import { Lock, Shield } from '../components/icons'
import type { MockPriceBreakdown } from '../mock-data'
import { formatPrice } from '../mock-data'

interface PaymentStepProps {
  priceBreakdown: MockPriceBreakdown | null
  isProcessing?: boolean
}

export function PaymentStep({ priceBreakdown, isProcessing = false }: PaymentStepProps) {
  const [cardNumber, setCardNumber] = useState('')
  const [expiry, setExpiry] = useState('')
  const [cvc, setCvc] = useState('')

  const totalDisplay = priceBreakdown
    ? formatPrice(priceBreakdown.totalCents, priceBreakdown.currencySymbol)
    : '$0.00'

  return (
    <div>
      <div className="bw2-section-label">Payment</div>

      {/* Total amount */}
      <div className="bw2-recessed bw2-recessed--glow" style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: '8px', padding: '8px 10px',
      }} role="status" aria-label={`Amount due: ${totalDisplay}`}>
        <span style={{ fontSize: '9px', color: 'var(--bw2-text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em', fontFamily: 'var(--bw2-font-label)' }}>
          Amount Due
        </span>
        <span style={{
          fontFamily: 'var(--bw2-font-display)', fontSize: '16px', fontWeight: 700,
          color: 'var(--bw2-text-accent)', textShadow: '0 0 8px var(--bw2-accent-glow)',
        }}>
          {totalDisplay}
        </span>
      </div>

      {/* Card form */}
      <div className="bw2-payment" role="form" aria-label="Payment details">
        <div className="bw2-form-field" style={{ marginBottom: '6px' }}>
          <label className="bw2-form-label" htmlFor="bw2-pay-card">Card Number</label>
          <input
            className="bw2-form-input"
            id="bw2-pay-card"
            type="text"
            value={cardNumber}
            onInput={e => setCardNumber((e.target as HTMLInputElement).value)}
            placeholder="4242 4242 4242 4242"
            maxLength={19}
            autoComplete="cc-number"
            inputMode="numeric"
            aria-required="true"
          />
        </div>
        <div className="bw2-form-grid">
          <div className="bw2-form-field">
            <label className="bw2-form-label" htmlFor="bw2-pay-expiry">Expiry</label>
            <input
              className="bw2-form-input"
              id="bw2-pay-expiry"
              type="text"
              value={expiry}
              onInput={e => setExpiry((e.target as HTMLInputElement).value)}
              placeholder="MM / YY"
              maxLength={7}
              autoComplete="cc-exp"
              inputMode="numeric"
              aria-required="true"
            />
          </div>
          <div className="bw2-form-field">
            <label className="bw2-form-label" htmlFor="bw2-pay-cvc">CVC</label>
            <input
              className="bw2-form-input"
              id="bw2-pay-cvc"
              type="text"
              value={cvc}
              onInput={e => setCvc((e.target as HTMLInputElement).value)}
              placeholder="123"
              maxLength={4}
              autoComplete="cc-csc"
              inputMode="numeric"
              aria-required="true"
            />
          </div>
        </div>

        {/* Mock notice */}
        <div style={{
          marginTop: '8px', padding: '5px 8px',
          background: 'rgba(210, 153, 34, 0.08)', borderRadius: '2px',
          border: '1px solid rgba(210, 153, 34, 0.15)',
          fontSize: '9px', color: 'var(--bw2-warning)',
          display: 'flex', alignItems: 'center', gap: '6px',
          fontFamily: 'var(--bw2-font-label)', textTransform: 'uppercase', letterSpacing: '0.08em',
        }} aria-hidden="true">
          <Shield size={10} />
          Mock mode — no real payment
        </div>
      </div>

      {/* Security badges */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: '10px', gap: '12px',
        fontSize: '8px', color: 'var(--bw2-text-muted)', fontFamily: 'var(--bw2-font-label)',
        textTransform: 'uppercase', letterSpacing: '0.1em',
      }} aria-hidden="true">
        <span><Lock size={8} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '3px' }} />SSL Encrypted</span>
        <span><Shield size={8} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '3px' }} />PCI Compliant</span>
      </div>
    </div>
  )
}
